import { useEffect, useState } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup, EditableCollectionBinding, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { unfold } from '../utils/motion';
import { useIsEditing } from '../utils/hooks';
import { PATHS, pagePath, scrollToId, tourCta, useSiteFlags } from '../utils/site';
import { ctaFor, faqCategories, faqStore, markHtml, markTerms, matches, toFaqItems, type Faq } from '../utils/faq';
import { useStore } from '../utils/store';
import { MARK_STYLES, Marked } from './FaqSearch';

const applyCtaDef: SlotGroupDefinition = {
  label: 'Apply Callout Button',
  description: 'Under an open Application Requirements, Credit & Finance or Leasing History answer. Hidden without an application URL.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Apply Now' },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_online_leasing_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'apply_now_clicked' },
  },
};

const tourCtaDef: SlotGroupDefinition = {
  label: 'Tour Callout Button',
  description: 'Under an open Renters Insurance answer. Without a self-guided tour URL the default becomes Contact Us.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Book Self-Guided Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const petsCtaDef: SlotGroupDefinition = {
  label: 'Pet Policy Callout Button',
  description: 'Under an open Pets & Animals answer. Hidden when the pet policy page is hidden.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'See Pet Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/pet-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const contactCtaDef: SlotGroupDefinition = {
  label: 'Contact Callout Button',
  description: 'Under an open fees, Vehicles or More questions answer.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Contact Us' },
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const FIRST_PAGE = 8;
const FOLD_MS = 300;
const SUGGESTED_TOPICS = ['Pets & Animals', 'Fees & Deposits', 'Application Requirements'];
const domId = (key: string) => `faq-${key.replace(/\W+/g, '-').toLowerCase()}`;

const calloutBox =
  'flex max-w-[680px] items-center justify-between gap-6 rounded-r-md border-l-[3px] border-l-[--q-secondary] bg-[--q-surface] py-5 pl-6 pr-5 [&_p]:m-0 max-[767px]:flex-col max-[767px]:items-stretch max-[767px]:gap-3.5 max-[767px]:p-[18px]';
const calloutTitle = 'flex-auto font-heading text-[20px] leading-7';
const answer =
  'max-w-[680px] text-[17px] leading-7 text-muted-foreground [&_a]:font-medium [&_a]:text-[--q-primary-on-paper] [&_a]:underline-offset-[3px] [&_li+li]:mt-1 [&_ol]:mx-0 [&_ol]:mb-[1em] [&_ol]:mt-0 [&_ol]:pl-[1.25em] [&_p:last-child]:mb-0 [&_p]:mx-0 [&_p]:mb-[1em] [&_p]:mt-0 [&_ul]:mx-0 [&_ul]:mb-[1em] [&_ul]:mt-0 [&_ul]:pl-[1.25em] max-[767px]:text-base max-[767px]:leading-[26px]';
const noneRow = 'flex items-center justify-between gap-4 text-[15px] leading-[22px]';
const noneLink = 'group/row inline-flex min-h-11 items-center gap-1.5 font-medium text-[--q-primary-on-paper] no-underline';
const noneArrow = 'btn__arrow group-hover/row:[transform:translateX(4px)]';

const Plus = ({ open }: { open: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className="fill-none stroke-current stroke-[1.6] [stroke-linecap:round]">
    <path d={open ? 'M3 8h10' : 'M3 8h10M8 3v10'} />
  </svg>
);

/** Every answer as an accordion, one open at a time, filtered by the search band and topic pills above it. */
export function FaqList() {
  const flags = useSiteFlags();
  const editing = useIsEditing();
  const faqs = useCollectionBinding<Faq>('faqlist.faqs', { collection: 'faqs' });
  const apply = useSlotGroup('faqlist.apply_cta', applyCtaDef);
  const tour = useSlotGroup('faqlist.tour_cta', tourCtaDef);
  const pets = useSlotGroup('faqlist.pets_cta', petsCtaDef);
  const contact = useSlotGroup('faqlist.contact_cta', contactCtaDef);
  const { query, category, focus } = useStore(faqStore);

  const items = toFaqItems(faqs);
  const q = query.trim();
  const filtered = items.filter((it) => (category === 'all' || it.category === category) && (!q || matches(it, q)));
  const narrowed = category !== 'all' || !!q;
  const terms = markTerms(items, q);

  // undefined until the visitor opens or closes one: until then the first visible answer is open.
  const [chosen, setChosen] = useState<string | null | undefined>(undefined);
  const openKey = chosen === undefined || (chosen && !filtered.some((it) => it.key === chosen)) ? filtered[0]?.key ?? null : chosen;
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setChosen(undefined);
  }, [query, category]);

  useEffect(() => {
    if (!focus) return;
    setChosen(focus);
    setExpanded(true);
    faqStore.set({ focus: null });
    const id = domId(focus);
    requestAnimationFrame(() => document.getElementById(id)?.querySelector('button')?.focus({ preventScroll: true }));
    // the answer that was open above folds for --q-motion-normal; scrolling sooner lands short
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.setTimeout(() => scrollToId(id), reduced ? 0 : FOLD_MS);
  }, [focus]);

  const shown = narrowed || expanded || editing ? filtered : filtered.slice(0, FIRST_PAGE);
  const more = filtered.length - shown.length;
  const applyHref = apply.url || flags.applyUrl;
  const tourLink = tourCta(tour, flags.selfTourUrl);
  const cats = faqCategories(items).map((c) => c.name);
  const suggested = [...SUGGESTED_TOPICS.filter((t) => cats.includes(t)), ...cats.filter((t) => !SUGGESTED_TOPICS.includes(t))].slice(0, 3);

  const status = !narrowed
    ? `Showing all ${items.length} answers`
    : q
      ? `${filtered.length} ${filtered.length === 1 ? 'answer' : 'answers'} for “${q}”`
      : `${filtered.length} ${filtered.length === 1 ? 'answer' : 'answers'} in ${category}`;

  const callout = (kind: ReturnType<typeof ctaFor>) => {
    if (kind === 'apply')
      return (
        <EditableSlotGroup groupPath="faqlist.apply_cta" definition={applyCtaDef}>
          {applyHref && (
            <div className={calloutBox}>
              <div className="flex flex-col gap-0.5">
                <RichTextContent path="faqlist.apply_title" as="div" className="font-heading text-[20px] leading-7 [&:only-child]:flex-auto" />
                <RichTextContent path="faqlist.apply_sub" as="div" className="text-sm leading-5 text-muted-foreground" />
              </div>
              <a className="btn btn-primary" href={applyHref} target={apply._target || '_self'} data-vibesite-event={apply.vibesite_event || undefined}>
                {apply.label || 'Apply Now'} <span className="btn__arrow">→</span>
              </a>
            </div>
          )}
        </EditableSlotGroup>
      );
    if (kind === 'tour')
      return (
        <EditableSlotGroup groupPath="faqlist.tour_cta" definition={tourCtaDef}>
          <div className={calloutBox}>
            <RichTextContent path="faqlist.tour_title" as="div" className={calloutTitle} />
            <a
              className="btn btn-primary"
              href={tourLink.href}
              target={tourLink.isTour ? tour._target || '_self' : '_self'}
              data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}
            >
              {tourLink.label}
            </a>
          </div>
        </EditableSlotGroup>
      );
    if (kind === 'pets')
      return (
        <EditableSlotGroup groupPath="faqlist.pets_cta" definition={petsCtaDef}>
          {pagePath('pet-policy') && (
            <div className={calloutBox}>
              <RichTextContent path="faqlist.pets_title" as="div" className={calloutTitle} />
              <a className="btn btn-primary" href={pets.url || PATHS.petPolicy} target={pets._target || '_self'}>
                {pets.label || 'See Pet Policy'} <span className="btn__arrow">→</span>
              </a>
            </div>
          )}
        </EditableSlotGroup>
      );
    return (
      <EditableSlotGroup groupPath="faqlist.contact_cta" definition={contactCtaDef}>
        <div className={calloutBox}>
          <RichTextContent path="faqlist.contact_title" as="div" className={calloutTitle} />
          <a className="btn btn-primary" href={contact.url || PATHS.contact} target={contact._target || '_self'}>
            {contact.label || 'Contact Us'} <span className="btn__arrow">→</span>
          </a>
        </div>
      </EditableSlotGroup>
    );
  };

  if (!items.length && !editing) return null;

  return (
    <section
      className={`flex justify-center bg-[--q-paper] px-[--gutter] pb-4xl pt-16 max-[767px]:pb-14 max-[767px]:pt-8 ${MARK_STYLES}`}
      id="faq-list"
    >
      <div className="w-full max-w-[880px]">
        <div className="pb-4 text-sm leading-5 text-muted-foreground max-[767px]:pb-3" aria-live="polite">{status}</div>
        <Accordion.Root type="single" collapsible value={openKey ?? ''} onValueChange={(v) => setChosen(v || null)}>
          <EditableCollectionBinding bindingPath="faqlist.faqs" label="FAQs" className="flex flex-col">
            {shown.map((it) => {
              const isOpen = openKey === it.key;
              const id = domId(it.key);
              return (
                <Accordion.Item key={it.key} value={it.key} id={id} className={`border-t ${isOpen ? 'border-t-[--q-ink]' : 'border-t-[--q-border]'}`}>
                  <Accordion.Header asChild>
                    <Accordion.Trigger
                      className={`group/faqhead grid w-full grid-cols-[minmax(0,1fr)_264px_44px] items-center gap-6 text-left text-inherit [grid-template-areas:'q_cat_icon'] max-[767px]:grid-cols-[minmax(0,1fr)_44px] max-[767px]:gap-x-4 max-[767px]:gap-y-1 max-[767px]:[grid-template-areas:'cat_icon'_'q_icon'] ${
                        isOpen ? 'pb-5 pt-7 max-[767px]:pb-4 max-[767px]:pt-5' : 'py-[22px] max-[767px]:py-4'
                      }`}
                    >
                      <span
                        className={`justify-self-end text-right text-[12px] font-medium uppercase leading-4 tracking-[--q-tracking-label] [grid-area:cat] max-[767px]:justify-self-start max-[767px]:text-left max-[767px]:text-[11px] max-[767px]:leading-[14px] ${
                          isOpen ? 'text-[--q-secondary]' : 'text-muted-foreground'
                        }`}
                      >
                        {it.category}
                      </span>
                      <span
                        className={`font-heading [grid-area:q] [transition:color_var(--q-motion-fast)] group-hover/faqhead:text-[--q-primary-on-paper] ${
                          isOpen
                            ? 'text-[28px] leading-9 tracking-[-0.01em] max-[767px]:text-[24px] max-[767px]:leading-[30px]'
                            : 'text-[24px] leading-8 max-[767px]:text-[20px] max-[767px]:leading-[26px]'
                        }`}
                      >
                        <Marked text={it.question} terms={terms} />
                      </span>
                      <span
                        className={`inline-flex h-11 w-11 items-center justify-center rounded-full border [grid-area:icon] [transition:background-color_var(--q-motion-fast),border-color_var(--q-motion-fast)] ${
                          isOpen ? 'border-primary bg-primary text-primary-foreground' : 'border-[--q-border] text-[--q-primary-on-paper]'
                        }`}
                      >
                        <Plus open={isOpen} />
                      </span>
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className={unfold}>
                    <div className="flex flex-col gap-5 pb-9 max-[767px]:gap-4 max-[767px]:pb-7">
                      <div className={answer} dangerouslySetInnerHTML={{ __html: markHtml(it.html, terms) }} />
                      {callout(ctaFor(it.category))}
                    </div>
                  </Accordion.Content>
                </Accordion.Item>
              );
            })}
          </EditableCollectionBinding>
        </Accordion.Root>
        {narrowed && !filtered.length && (
          <div className="flex flex-col gap-[18px] rounded-lg bg-[--q-surface] p-8 max-[767px]:px-5 max-[767px]:py-6">
            <div className="font-heading text-[26px] leading-8 max-[767px]:text-[22px] max-[767px]:leading-7">No answers mention “{q}” yet.</div>
            <RichTextContent path="faqlist.empty" as="p" className="m-0 text-[15px] leading-[22px] text-muted-foreground [&_p]:m-0" />
            {suggested.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {suggested.map((t) => (
                  <button
                    key={t}
                    className="inline-flex h-11 items-center rounded-full border border-[--q-border] bg-[--q-paper] px-4 text-sm font-medium leading-5 text-[--q-ink] hover:border-[--q-primary-on-paper]"
                    type="button"
                    onClick={() => faqStore.set({ query: '', category: t, focus: null })}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
            {pagePath('amenities') && (
              <div className={`${noneRow} border-t border-t-[--q-border] pt-[18px]`}>
                <span>Looking for amenities?</span>
                <a className={noneLink} href={PATHS.amenities}>See Amenities <span className={noneArrow}>→</span></a>
              </div>
            )}
            <div className={noneRow}>
              <span>Or ask us directly</span>
              <a className={noneLink} href={PATHS.contact}>Contact Us <span className={noneArrow}>→</span></a>
            </div>
          </div>
        )}
        <div className="flex flex-col items-center gap-3 border-t border-t-[--q-border] pt-8 max-[767px]:pt-6">
          {more > 0 && (
            <button className="btn btn-secondary" type="button" onClick={() => setExpanded(true)}>
              Show {more} more {more === 1 ? 'answer' : 'answers'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqList',
  displayName: 'FAQ List',
  tokens: {
    'faqlist.apply_title': { label: 'Apply Callout Title', type: 'richtext', defaultValue: '<p>Found your next home?</p>' },
    'faqlist.apply_sub': {
      label: 'Apply Callout Line',
      type: 'richtext',
      defaultValue: '<p>Apply online in a few minutes</p>',
    },
    'faqlist.tour_title': { label: 'Tour Callout Title', type: 'richtext', defaultValue: '<p>Ready to see it for yourself?</p>' },
    'faqlist.pets_title': { label: 'Pet Callout Title', type: 'richtext', defaultValue: '<p>Bringing a pet?</p>' },
    'faqlist.contact_title': { label: 'Contact Callout Title', type: 'richtext', defaultValue: '<p>Still have questions?</p>' },
    'faqlist.empty': { label: 'No Results Text', type: 'richtext', defaultValue: '<p>Try a shorter word, or one of these topics:</p>' },
  },
  slotGroups: {
    'faqlist.apply_cta': applyCtaDef,
    'faqlist.tour_cta': tourCtaDef,
    'faqlist.pets_cta': petsCtaDef,
    'faqlist.contact_cta': contactCtaDef,
  },
  collectionBindings: {
    'faqlist.faqs': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FaqList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FaqList;
